// components/LegalRepresentatives/ManagedAccountsModal.js
import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { formatDate } from '../service/utils/formatters';
import legalRepresentativeServices from '../service/api/legalRepresentativeServices';
import { useDebounce } from '../service/Hooks/useDebounce';

const ManagedAccountsModal = ({ isOpen, onClose, representative }) => {
  const [managedAccounts, setManagedAccounts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  
  // États pour les filtres
   // ÉTATS MODIFIÉS : Séparer la recherche input et la recherche debounced
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500); // AJOUTER CETTE LIGNE
  
  // États pour les autres filtres (inchangés)
  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 10,
    status: '',
    mandateType: '',
    sortBy: 'StartDate',
    sortDescending: true
  });

  // EFFET MODIFIÉ : Utiliser debouncedSearch au lieu de filters.search
  useEffect(() => {
    if (isOpen && representative) {
      fetchManagedAccounts();
    } else {
      // Réinitialiser les états quand le modal se ferme
      setManagedAccounts([]);
      setPagination(null);
      setError(null);
      setNoResults(false);
      setSearchInput(''); // AJOUTER : reset de la recherche
    }
  }, [isOpen, representative, debouncedSearch, filters.status, filters.mandateType, filters.pageNumber, filters.pageSize]); // MODIFIER LES DÉPENDANCES

  // FONCTION fetchManagedAccounts MODIFIÉE : Utiliser debouncedSearch
  const fetchManagedAccounts = async () => {
    if (!representative?.accountId) {
      setError("Aucun représentant sélectionné");
      return;
    }

    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      // COMBINER les filtres avec la recherche debounced
      const combinedFilters = {
        ...filters,
        search: debouncedSearch // UTILISER debouncedSearch au lieu de filters.search
      };

      const result = await legalRepresentativeServices.getManagedAccountsByRepresentative(
        representative.accountId,
        combinedFilters
      );

      if (result.success) {
        setManagedAccounts(result.data || []);
        setPagination(result.pagination);
        
        // Vérifier s'il y a des résultats
        if (!result.data || result.data.length === 0) {
          setNoResults(true);
        }
      } else {
        setError(result.error || "Erreur lors du chargement des comptes administrés");
        setNoResults(true);
      }
    } catch (error) {
      console.error('Error fetching managed accounts:', error);
      setError("Erreur de connexion au serveur");
      setNoResults(true);
    } finally {
      setLoading(false);
    }
  };

  // FONCTION handleFilterChange MODIFIÉE : Gérer séparément la recherche
  const handleFilterChange = (filterName, value) => {
    if (filterName === 'search') {
      // Pour la recherche : mettre à jour l'input seulement, le debounce s'occupe du reste
      setSearchInput(value);
      // Reset à la page 1 quand la recherche change
      setFilters(prev => ({ ...prev, pageNumber: 1 }));
    } else {
      // Pour les autres filtres : comportement normal
      setFilters(prev => ({
        ...prev,
        [filterName]: value,
        pageNumber: 1
      }));
    }
  };

  // FONCTIONS INCHANGÉES :
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, pageNumber: newPage }));
  };

  const handleResetFilters = () => {
    setSearchInput(''); // AJOUTER : reset de la recherche input
    setFilters({
      pageNumber: 1,
      pageSize: 10,
      status: '',
      mandateType: '',
      sortBy: 'StartDate',
      sortDescending: true
    });
  };


  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-custom modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          {/* En-tête du modal */}
          <div className="modal-header bg-light">
            <div className="d-flex align-items-center">
              <i className="fas fa-user-cog me-2 text-primary"></i>
              <h5 className="modal-title text-dark mb-0">
                Comptes Administrés par {representative?.displayName}
                {pagination && (
                  <span className="badge bg-primary ms-2">
                    {pagination.totalCount || 0}
                  </span>
                )}
              </h5>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>

          {/* Corps du modal */}
           <div className="modal-body">
            
            {/* Barre de filtres MODIFIÉE : Indicateur de debounce */}
            <div className="card mb-4 border-0 shadow-sm">
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  {/* Recherche AVEC INDICATEUR */}
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">
                      Rechercher
                      {searchInput !== debouncedSearch && searchInput && (
                        <span className="ms-1 text-warning" title="Recherche en cours...">
                          <i className="fas fa-circle-notch fa-spin"></i>
                        </span>
                      )}
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Nom du compte..."
                        value={searchInput} // AFFICHER searchInput au lieu de filters.search
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    {searchInput && (
                      <small className="text-muted">
                        Recherche : "{debouncedSearch || 'en cours...'}"
                      </small>
                    )}
                  </div>

                  {/* Les autres filtres RESTENT IDENTIQUES */}
                  <div className="col-md-3">
                    <select
                      className="account-type-select"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Tous les statuts</option>
                      <option value="ACTIVE">Actif</option>
                      <option value="INACTIVE">Inactif</option>
                      <option value="PENDING">En attente</option>
                    </select>
                  </div>

                  <div className="col-md-2">
                    <select
                      className="account-type-select"
                      value={filters.mandateType}
                      onChange={(e) => handleFilterChange('mandateType', e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Tous les types</option>
                      <option value="Tuteur">Tuteur</option>
                      <option value="Curateur">Curateur</option>
                      <option value="Mandataire">Mandataire</option>
                      <option value="Représentant">Représentant</option>
                    </select>
                  </div>

                  <div className="col-md-1 d-flex align-items-end">
                    <button
                      type="button"
                      className="filter-button"
                      onClick={handleResetFilters}
                      disabled={loading}
                    >
                      <i className="fas fa-refresh me-1"></i>
                      Réinitialiser
                    </button>
                  </div>
                </div>
              </div>
            </div>


            {/* État de chargement */}
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2 text-muted">Chargement des comptes administrés...</p>
                {debouncedSearch && (
                  <small className="text-muted">
                    Recherche : "{debouncedSearch}"
                  </small>
                )}
              </div>
            )}

            {/* Message d'erreur */}
            {error && !loading && (
              <div className="alert alert-danger text-center">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </div>
            )}

            {/* Aucun résultat */}
            {noResults && !loading && !error && (
              <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">Aucun compte administré trouvé</h5>
                <p className="text-muted">
                  {representative?.displayName} ne gère actuellement aucun compte.
                </p>
              </div>
            )}

            {/* Tableau des comptes administrés */}
            {!loading && !error && !noResults && managedAccounts.length > 0 && (
              <>
                <div className="table-responsive">
                  <table className="table table-hover table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Compte</th>
                        <th>Type Mandat</th>
                        <th>Relation</th>
                        <th>Date Début</th>
                        <th>Date Fin</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {managedAccounts.map((account) => (
                        <tr key={account.id}>
                          <td>
                            <div className="fw-semibold">{account.displayName}</div>
                            {account.email && (
                              <small className="text-muted">{account.email}</small>
                            )}
                          </td>
                          <td>
                            <span className="badge rounded-pill bg-white bg-opacity-10 text-primary border border-white border-opacity-25">
                              {account.mandateType}
                            </span>
                          </td>
                          <td>{account.relationshipType}</td>
                          <td>
                            <small className="text-muted">
                              {formatDate(account.startDate)}
                            </small>
                          </td>
                          <td>
                            <small className="text-muted">
                              {account.endDate ? formatDate(account.endDate) : 'Indéfinie'}
                            </small>
                          </td>
                          <td>
                            <span className={`badge rounded-pill ${
                              account.status === 'ACTIVE' 
                                ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25'
                                : account.status === 'PENDING'
                                ? 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25'
                                : 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
                            }`}>
                              {account.status === 'ACTIVE' ? 'Actif' : 
                               account.status === 'PENDING' ? 'En attente' : 'Inactif'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div>
                      <small className="text-muted">
                        Affichage de {((filters.pageNumber - 1) * filters.pageSize) + 1} à{' '}
                        {Math.min(filters.pageNumber * filters.pageSize, pagination.totalCount)} sur{' '}
                        {pagination.totalCount} compte(s)
                      </small>
                    </div>
                    
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        {/* Bouton précédent */}
                        <li className={`page-item ${filters.pageNumber <= 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(filters.pageNumber - 1)}
                            disabled={filters.pageNumber <= 1 || loading}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        </li>

                        {/* Pages */}
                        {[...Array(pagination.totalPages)].map((_, index) => {
                          const pageNum = index + 1;
                          // Afficher seulement les pages proches de la page courante
                          if (
                            pageNum === 1 ||
                            pageNum === pagination.totalPages ||
                            (pageNum >= filters.pageNumber - 1 && pageNum <= filters.pageNumber + 1)
                          ) {
                            return (
                              <li
                                key={pageNum}
                                className={`page-item ${filters.pageNumber === pageNum ? 'active' : ''}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(pageNum)}
                                  disabled={loading}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          } else if (
                            pageNum === filters.pageNumber - 2 ||
                            pageNum === filters.pageNumber + 2
                          ) {
                            return (
                              <li key={pageNum} className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            );
                          }
                          return null;
                        })}

                        {/* Bouton suivant */}
                        <li className={`page-item ${filters.pageNumber >= pagination.totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(filters.pageNumber + 1)}
                            disabled={filters.pageNumber >= pagination.totalPages || loading}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>

                    {/* Sélecteur de taille de page */}
                    <div className="d-flex align-items-center">
                      <small className="text-muted me-2">Lignes par page:</small>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: '80px' }}
                        value={filters.pageSize}
                        onChange={(e) => handleFilterChange('pageSize', parseInt(e.target.value))}
                        disabled={loading}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pied de page */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              <i className="fas fa-times me-1"></i>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ManagedAccountsModal;